import api from '../api/axios';

export interface Request {
  id: number;
  attributes?: any;
  requestNumber?: string;
  customer?: any;
  insuranceClient?: any;
  serviceProvider?: any;
  previousServiceProvider?: any;
  status?: 'pending' | 'assigned' | 'active' | 'invoice_sent' | 'payment_received' | 'completed' | 'cancelled';
  description?: string;
  phoneNumber?: string;
  scheduledDate?: string;
  completedDate?: string;
  assignedAt?: string;
  invoiceSentAt?: string;
  paymentReceivedAt?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  notes?: string;
  createdByAI?: boolean;
  aiTranscript?: string;
  requiresPayment?: boolean;
  invoice?: any;
  cancelReason?: string;
  providerChangeReason?: string;
  serviceType?: string;
  serviceCode?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  locationText?: string;
  locationPending?: boolean;
  toLocationLatitude?: number;
  toLocationLongitude?: number;
  toLocationText?: string;
  toDropDown?: string;
  evidencePhotoBefore?: number | any;
  evidencePhotoAfter?: number | any;
  reasons?: string[];
  imported?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const requestService = {
  async getAll(params?: any) {
    const response = await api.get('/requests', { params });
    return response.data;
  },

  /** Paginated list for /requests page. Tries /requests/list first (correct pagination); falls back to /requests if 404 (e.g. server not yet deployed). */
  async getList(params?: any) {
    try {
      const response = await api.get('/requests/list', { params });
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        const response = await api.get('/requests', { params });
        return response.data;
      }
      throw err;
    }
  },

  async getLogs(requestId: number) {
    const response = await api.get(`/request-logs/by-request/${requestId}`);
    return response.data;
  },
  
  async createInvoiceFromRequest(
    requestId: number, 
    items: any[], 
    dueDate?: string, 
    notes?: string, 
    reasons?: string[],
    toLocationLatitude?: number | null,
    toLocationLongitude?: number | null,
    toLocationText?: string
  ) {
    const response = await api.post('/invoices/create-from-request', {
      requestId,
      items,
      dueDate,
      notes,
      reasons,
      toLocationLatitude,
      toLocationLongitude,
      toLocationText
    });
    return response.data;
  },

  async getById(id: number) {
    const response = await api.get(`/requests/${id}`, {
      params: {
        'populate[customer]': '*',
        'populate[insuranceClient][populate][insuranceCompany]': '*',
        'populate[insuranceClient][populate][servicePackage]': '*',
        'populate[serviceProvider]': '*',
        'populate[previousServiceProvider]': '*',
        'populate[invoice]': '*',
        'populate[invoices]': '*',
        'populate[evidencePhotoBefore]': '*',
        'populate[evidencePhotoAfter]': '*'
      }
    });
    return response.data;
  },

  async create(data: Partial<Request>) {
    // Clean up empty strings for relations - they should be null or undefined
    const cleanData = { ...data };
    if (cleanData.serviceProvider === '' || cleanData.serviceProvider === null) {
      delete cleanData.serviceProvider;
    }
    if (cleanData.customer === '' || cleanData.customer === null) {
      delete cleanData.customer;
    }
    if (cleanData.invoice === '' || cleanData.invoice === null) {
      delete cleanData.invoice;
    }
    const response = await api.post('/requests', { data: cleanData });
    return response.data;
  },

  async update(id: number, data: Partial<Request>) {
    // Clean up empty strings for relations - they should be null or undefined
    const cleanData = { ...data };
    if (cleanData.serviceProvider === '' || cleanData.serviceProvider === null) {
      delete cleanData.serviceProvider;
    }
    if (cleanData.customer === '' || cleanData.customer === null) {
      delete cleanData.customer;
    }
    if (cleanData.invoice === '' || cleanData.invoice === null) {
      delete cleanData.invoice;
    }
    const response = await api.put(`/requests/${id}`, { data: cleanData });
    return response.data;
  },

  async delete(id: number) {
    const response = await api.delete(`/requests/${id}`);
    return response.data;
  },

  async getPending(params?: any) {
    const response = await api.get('/requests', {
      params: {
        ...params,
        filters: { status: { $eq: 'pending' } },
        populate: ['customer', 'serviceProvider']
      }
    });
    return response.data;
  },

  async getActive(params?: any) {
    const response = await api.get('/requests', {
      params: {
        ...params,
        filters: { status: { $in: ['assigned', 'active', 'invoice_sent', 'payment_received'] } },
        populate: ['customer', 'serviceProvider', 'invoice', 'previousServiceProvider']
      }
    });
    return response.data;
  },

  async getDone(params?: any) {
    const response = await api.get('/requests', {
      params: {
        ...params,
        filters: { status: { $eq: 'completed' } },
        populate: ['customer', 'serviceProvider', 'invoice', 'previousServiceProvider']
      }
    });
    return response.data;
  },

  async assignProvider(requestId: number, providerId: number) {
    // Use a clean PUT request without any query parameters that might interfere
    const response = await api.put(`/requests/${requestId}`, {
      data: {
        serviceProvider: providerId,
        status: 'assigned',
        assignedAt: new Date().toISOString()
      }
    }, {
      params: {} // Explicitly set empty params to avoid any populate issues
    });
    return response.data;
  },

  async changeProvider(requestId: number, newProviderId: number, reason: string) {
    // Get current request to find current provider ID
    try {
      const request = await this.getById(requestId);
      const currentProviderId = request.data?.attributes?.serviceProvider?.data?.id || 
                                request.data?.attributes?.serviceProvider?.id ||
                                request.attributes?.serviceProvider?.data?.id ||
                                request.attributes?.serviceProvider?.id;
      
      const response = await api.put(`/requests/${requestId}`, {
        data: {
          previousServiceProvider: currentProviderId || undefined,
          serviceProvider: newProviderId,
          providerChangeReason: reason
        }
      }, {
        params: {} // Explicitly set empty params to avoid any populate issues
      });
      return response.data;
    } catch (error) {
      // If getById fails, try to update anyway without previous provider
      const response = await api.put(`/requests/${requestId}`, {
        data: {
          serviceProvider: newProviderId,
          providerChangeReason: reason
        }
      }, {
        params: {} // Explicitly set empty params to avoid any populate issues
      });
      return response.data;
    }
  },

  async import(data: any[]) {
    const response = await api.post('/requests/import', { data });
    return response.data;
  },

  // Stream-based import with real-time progress
  getImportStreamUrl(): string {
    const baseUrl = api.defaults.baseURL || '';
    return `${baseUrl}/requests/import-stream`;
  }
};

